import { awaitJSON, DigitalAuditLambdaFetch, fetchPresigned } from '@src/utils/fetch-utils';
import { FullScanJobStatus, FullScanResult } from './full-scan-constants';

// The raw wire shape getFullScanJob can return - `resultUrl` is an internal transport detail
// (bpm-lambdas always offloads a job's result to S3 and hands back a presigned URL instead of
// returning it inline) that this module resolves before handing a plain FullScanJobStatus to every
// other caller, so nothing downstream (the polling hook, the merge effect, the status banner)
// needs to know this path exists. `resultUrl` is absent while the job has no result yet.
interface FullScanJobWireStatus extends Omit<FullScanJobStatus, 'result'> {
  result: FullScanResult | null;
  resultUrl?: string | null;
}

export const startFullScan = async (
  url: string,
  consentSelector?: string
): Promise<{ jobId: string }> => {
  const res = await DigitalAuditLambdaFetch('/digital-audit/scan/full', {
    method: 'POST',
    body: { url, ...(consentSelector ? { consentAcceptSelector: consentSelector } : {}) }
  });
  return awaitJSON<{ jobId: string }>(res);
};

export const getFullScanJob = async (jobId: string): Promise<FullScanJobStatus> => {
  const res = await DigitalAuditLambdaFetch(`/digital-audit/scan/full/${jobId}`, {
    method: 'GET'
  });
  const { resultUrl, ...job } = await awaitJSON<FullScanJobWireStatus>(res);
  if (!resultUrl) {
    return job;
  }
  // Fetched directly from S3, not through DigitalAuditLambdaFetch - no auth header needed, same as
  // this app's other direct-to-S3 reads (e.g. S3SignedUrlFetch). A failure here (network blip,
  // URL expiry) is treated the same as "result not ready yet" rather than rethrown: the job itself
  // is DONE/PARTIAL in Postgres regardless, so surfacing this as a query error would flip
  // FullScanStatusBanner to "couldn't complete" for a job that's actually fine - better to let the
  // next 3s poll tick retry the download than show a spurious failure.
  try {
    const result = await awaitJSON<FullScanResult>(
      await fetchPresigned(resultUrl, 'Full scan result download failed')
    );
    return { ...job, result };
  } catch (e) {
    console.error('Failed to fetch offloaded full-scan result from S3', e);
    return { ...job, result: null };
  }
};
